import { Trans } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Drawer from '@/design_system/Drawer';
import { DrawerBody, DrawerHeader } from '@/design_system/Drawer/Drawer';
import { Footer, FooterButtons } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import Title from '@/design_system/Typography/Title';
import { ArticleContextData, useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { ArticlePhotoUpload } from '@/routes/Requests/New/components/ArticlePhotoUpload';
import { ErrorBoundary } from '@/services/sentry';

import { ColorSizeWeight } from './ColorSizeWeight';
import { EstimatedValue } from './EstimatedValue';
import { ProductAttributeValue } from './ProductAttributeValue';
import ProductInfo from './ProductInfo';
import { RetailPrice } from './RetailPrice';
import { UniqueArticle } from './UniqueArticle';

export const ArticleInformationFormDrawer = () => {
  const { state, view, productAttributes, iam } = useArticleContext();

  const closeDrawer = () => {
    const { hasError } = state.errors.check({
      details: articleDetailsErrors(view),
    });

    if (hasError) {
      return;
    }

    state.infoPanel.setIsOpen(false);
    state.errors.check(null);
  };

  return (
    <Drawer
      isOpen={state.infoPanel.isOpen}
      onOpenChange={(isOpen) => {
        if (isOpen) {
          state.infoPanel.setIsOpen(true);
        } else {
          closeDrawer();
        }
      }}
      style={{
        maxWidth: '31.25rem',
      }}
    >
      <DrawerHeader>
        <Title size="300" as="h1">
          <Trans id="article.edit.title">Edit information</Trans>
        </Title>
      </DrawerHeader>

      <DrawerBody>
        <ErrorBoundary>
          <Stack gap="1rem" padding="1rem 1.5rem">
            <ArticlePhotoUpload secondaryActionIconOnly />

            {view.details.serialNumber.shown && <UniqueArticle />}

            {!iam.hasOnlyExternalWorkshopScopeOnArticle && (
              <ProductInfo row={false} showReference />
            )}

            {productAttributes?.map((productAttribute) => (
              <ProductAttributeValue
                key={productAttribute.id}
                productAttribute={productAttribute}
              />
            ))}

            <ColorSizeWeight />

            <RetailPrice />

            <EstimatedValue />
          </Stack>
        </ErrorBoundary>
      </DrawerBody>

      <Footer>
        <FooterButtons>
          <Button onPress={closeDrawer}>
            <Trans id="article.edit.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </Drawer>
  );
};

export const articleDetailsErrors = (view: ArticleContextData['view']) => ({
  product: true,
  productAttributes: {
    missingValue: true,
  },
  missingBrand: view.details.brand.shown && view.details.brand.required,
  otherBrandEmpty: view.details.brand.shown,
  missingColor: view.details.color.shown && view.details.color.required,
  missingSize: view.details.size.shown && view.details.size.required,
  missingWeight: view.details.weight.shown && view.details.weight.required,
  missingArticlePhoto: view.details.articlePhoto.shown && view.details.articlePhoto.required,
  missingArticlePhotoForRefashion: view.details.articlePhoto.shown,
  missingEstimatedValue: view.details.estimatedValue.shown && view.details.estimatedValue.required,
});
